import React, { useCallback, useState } from 'react'
import {
  DialogContentText,
  Button,
  Card,
  CardActions,
  CardActionArea,
  CardContent,
  InputLabel,
  Typography,
  CircularProgress,
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { FileDrop } from 'react-file-drop'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import { ResumeUploadButtonCallback } from '../../Buttons/ResumeUpload'
import { titleCased } from '../../../utils/string'
import { SPINNER_SIZE, WidgetTRefs } from '../../../types/widget'
import useLeadActions, {
  allowedUploadExtensions,
} from '../../../hooks/useLeadActions'
import { useBETelemetry } from '../../../hooks/useTelemetry'
import useSiteData from '../../../hooks/useSiteData'
import DragAndDropSVG from '../../../images/drag-and-drop.inline.svg'

export default function DesktopUploadWidget({
  onChange,
  resumeApiStatus,
}: {
  onChange: ResumeUploadButtonCallback
  resumeApiStatus: 'idle' | 'submitting' | 'error' | 'success'
}): JSX.Element {
  const { errors } = useLeadActions()
  const [draggedFileOver, setDraggedFileOver] = useState(false)
  const dragEnter = useCallback(() => setDraggedFileOver(true), [])
  const dragLeave = useCallback(() => setDraggedFileOver(false), [])
  const { t } = useTranslation()
  const { classes, cx } = useStyles()
  const { default_resume_product: defaultResumeProduct } = useSiteData()
  const telemetry = useBETelemetry()

  return (
    <>
      <Card className={classes.root}>
        <CardActionArea
          className={cx(
            classes.actionArea,
            draggedFileOver &&
              resumeApiStatus !== 'submitting' &&
              classes.actionAreaHighlighted
          )}
          disabled={resumeApiStatus === 'submitting'}
        >
          <FileDrop
            onDrop={(_, event) => {
              // Assuming 'event' contains the necessary file information for the onChange logic
              onChange(
                // @ts-ignore
                event
              )
              // Now add the telemetry tracking
              telemetry.track({
                event: 'try_upload_resume',
              })
            }}
            onFrameDragEnter={dragEnter}
            onFrameDragLeave={dragLeave}
            onFrameDrop={dragLeave}
          >
            <InputLabel className={classes.invisibleInput}>
              <input
                type="file"
                // @ts-ignore
                onChange={onChange}
                accept={allowedUploadExtensions.join(', ')}
                // Null out the value to fire the `onChange` event
                onClick={(event) => {
                  event.currentTarget.value = ''
                  telemetry.track({ event: 'try_upload_resume' }) // Track the "Buy Now" event
                }}
                hidden
              />
            </InputLabel>
            <CardContent className={cx(classes.content)}>
              {resumeApiStatus === 'submitting' ? (
                <CircularProgress
                  className={classes.spinner}
                  color="secondary"
                  size={SPINNER_SIZE}
                />
              ) : (
                <CardContentJSX errored={!!errors.resumeFile} />
              )}
            </CardContent>
          </FileDrop>
        </CardActionArea>
        <CardActions className={classes.actions}>
          <Button
            className={classes.cta}
            color="primary"
            component="label"
            variant="contained"
            disabled={resumeApiStatus === 'submitting'}
            onClick={() => {
              telemetry.track({
                event: 'get_my_free_review_click_to_submit_resume',
              })
            }}
          >
            <input
              type="file"
              hidden
              // @ts-ignore
              onChange={onChange}
              accept={allowedUploadExtensions.join(', ')}
              // Null out the value to fire the `onChange` event
              onClick={(event) => {
                event.stopPropagation()
                event.currentTarget.value = ''
                telemetry.track({ event: 'try_upload_resume' }) // Track the "Buy Now" event
              }}
            />
            {resumeApiStatus === 'submitting'
              ? t(`${WidgetTRefs.DesktopTRef}.ctaTextLoading`, {
                  Product: titleCased(defaultResumeProduct),
                })
              : t(`${WidgetTRefs.DesktopTRef}.ctaText`, {
                  Product: titleCased(defaultResumeProduct),
                })}
          </Button>
        </CardActions>
      </Card>
    </>
  )
}

function CardContentJSX({ errored = false }: { errored?: boolean }): JSX.Element {
  const { classes, cx } = useCardContentStyles()
  const { t } = useTranslation()
  const { default_resume_product: defaultResumeProduct } = useSiteData()

  if (errored) {
    return (
      <>
        <Typography component="h6" className={classes.errorTitle}>
          {t(`${WidgetTRefs.DesktopTRef}.widgetText.errorHeading`)}
        </Typography>
        <Typography className={cx(classes.contentText, classes.errorHelperText)}>
          {t(`${WidgetTRefs.CommonTRef}.formatError`)}
        </Typography>
      </>
    )
  }

  return (
    <>
      <DragAndDropSVG />
      <DialogContentText className={cx('helperText', classes.contentText)}>
        {t(`${WidgetTRefs.DesktopTRef}.widgetText.helpCaption`, {
          defaultResumeProduct,
        })}
      </DialogContentText>
    </>
  )
}

const useStyles = makeStyles()((theme) => ({
  root: {
    background: theme.palette.background.default,
    borderRadius: '12px',
    boxShadow:
      '0px 8px 20px -4px rgb(15 56 113 / 0.12), 0px 2px 4px -1px rgb(15 56 113 / 0.08)',
    width: 420,
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  actionArea: {
    minHeight: 144,
    border: `1.5px dashed ${theme.palette.secondary.dark}`,
    borderRadius: '6px',

    '&:hover': {
      border: `1.5px solid ${theme.palette.primary.main}`,
      background: theme.colors.blue[200],

      '& .helperText': {
        color: theme.palette.primary.main,
      },
    },
  },
  actionAreaHighlighted: {
    border: `1.5px solid ${theme.palette.primary.main}`,
    background: theme.colors.blue[200],

    '& .helperText': {
      color: theme.palette.primary.main,
    },
  },
  invisibleInput: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    display: 'block',
    cursor: 'pointer',
  },
  actions: {
    padding: 0,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  cta: {
    width: '100%',

    '&.Mui-disabled': {
      '&&': {
        background: theme.colors.blue[200],
        color: theme.palette.primary.main,
        border: 'none',
        opacity: 1,
      },
    },
  },
  noVisibility: {
    '& *': {
      visibility: 'hidden',
    },
  },
  spinner: {
    opacity: 0.5,
  },
}))

const useCardContentStyles = makeStyles()((theme) => ({
  errorTitle: {
    color: theme.colors.red[60],

    '&&': {
      fontSize: '1.05em',
      lineHeight: 1.429,
    },
  },
  contentText: {
    '&&': {
      fontSize: '.9em',
      color: theme.palette.secondary.dark,
      margin: 0,
      lineHeight: '1.7137em',
    },
  },
  errorHelperText: {
    maxWidth: 232,
  },
}))
